import { MapPin, Plus, X, PawPrint } from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";

const FIELD =
  "w-full bg-white border border-sand-200 rounded-lg px-3 py-2.5 text-sm text-charcoal-800 focus:outline-none focus:ring-2 focus:ring-sage/50";
const LABEL = "text-sm font-medium text-charcoal-600 mb-1.5 block";

const TOLL_OPTS = [
  ["avoid", "Avoid tolls where safe"],
  ["balanced", "Balanced (recommended)"],
  ["fastest", "Prefer main highways"],
];

// Reusable trip planning form. `trip` is controlled state; `set` updates a field.
export default function TripForm({ trip, set, addons = [], showClientFields = false }) {
  const stops = trip.stops || [];
  const selected = new Set(trip.addons || []);
  const [routePoints, setRoutePoints] = useState([]);

  useEffect(() => {
    api.get("/destinations")
      .then((r) => setRoutePoints((r.data || []).filter((d) => d.type === "route").map((d) => d.name)))
      .catch(() => {});
  }, []);

  const toggleAddon = (key) => {
    const next = new Set(selected);
    next.has(key) ? next.delete(key) : next.add(key);
    set("addons", Array.from(next));
  };

  return (
    <div className="space-y-5">
      {showClientFields && (
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className={LABEL}>Your name</label>
            <input data-testid="trip-client-name" className={FIELD} value={trip.client_name || ""}
              onChange={(e) => set("client_name", e.target.value)} />
          </div>
          <div>
            <label className={LABEL}>Your email</label>
            <input data-testid="trip-client-email" className={FIELD} value={trip.client_email || ""}
              onChange={(e) => set("client_email", e.target.value)} />
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <datalist id="route-points">
          {routePoints.map((n) => <option key={n} value={n} />)}
        </datalist>
        <div>
          <label className={LABEL}><MapPin size={13} className="inline mr-1" />Start point</label>
          <input data-testid="trip-start" className={FIELD} placeholder="e.g. San Antonio, TX or Mérida" list="route-points"
            value={trip.start || ""} onChange={(e) => set("start", e.target.value)} />
        </div>
        <div>
          <label className={LABEL}>
            <MapPin size={13} className="inline mr-1" />Destination
            <button type="button" data-testid="trip-swap" title="Swap direction"
              onClick={() => { const s = trip.start; set("start", trip.end || ""); set("end", s || ""); }}
              className="ml-2 rounded-full border border-sand-300 px-2 py-0.5 text-[11px] text-charcoal-600 hover:border-terracotta hover:text-terracotta">
              ⇄ swap direction
            </button>
          </label>
          <input data-testid="trip-end" className={FIELD} placeholder="e.g. Mérida or Laredo border" list="route-points"
            value={trip.end || ""} onChange={(e) => set("end", e.target.value)} />
        </div>
      </div>

      <div>
        <label className={LABEL}>Optional stops</label>
        <div className="space-y-2">
          {stops.map((s, i) => (
            <div key={i} className="flex gap-2">
              <input data-testid={`trip-stop-${i}`} className={FIELD} value={s}
                onChange={(e) => { const n = [...stops]; n[i] = e.target.value; set("stops", n); }} />
              <button type="button" data-testid={`trip-stop-remove-${i}`}
                onClick={() => set("stops", stops.filter((_, j) => j !== i))}
                className="shrink-0 rounded-lg border border-sand-200 px-3 text-charcoal-400 hover:text-terracotta">
                <X size={16} />
              </button>
            </div>
          ))}
          <button type="button" data-testid="trip-add-stop" onClick={() => set("stops", [...stops, ""])}
            className="inline-flex items-center gap-1.5 text-sm text-terracotta font-medium hover:underline">
            <Plus size={14} /> Add a stop
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className={LABEL}>Preferred move date</label>
          <input data-testid="trip-move-date" type="date" className={FIELD} value={trip.move_date || ""}
            onChange={(e) => set("move_date", e.target.value)} />
        </div>
        <div>
          <label className={LABEL}>Toll preference</label>
          <select data-testid="trip-toll" className={FIELD} value={trip.toll_preference || "balanced"}
            onChange={(e) => set("toll_preference", e.target.value)}>
            {TOLL_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className={LABEL}>Things you'd like to see on the way (optional)</label>
        <textarea data-testid="trip-interests" rows={2} className={FIELD} value={trip.interests || ""}
          onChange={(e) => set("interests", e.target.value)} placeholder="Scenic towns, cenotes, food stops…" />
      </div>

      {/* Pets */}
      <div className="rounded-xl border border-sand-200 p-4 bg-sand-50/50">
        <label className="flex items-center gap-2 text-sm font-medium text-charcoal-700">
          <input data-testid="trip-pets" type="checkbox" checked={!!trip.pets}
            onChange={(e) => set("pets", e.target.checked)} className="accent-terracotta" />
          <PawPrint size={15} /> Traveling with pets
        </label>
        {trip.pets && (
          <div className="mt-3 grid sm:grid-cols-3 gap-3">
            <div>
              <label className={LABEL}>Number of pets</label>
              <input data-testid="trip-pet-count" type="number" min="0" className={FIELD}
                value={trip.pet_count || 0} onChange={(e) => set("pet_count", Number(e.target.value))} />
            </div>
            <div>
              <label className={LABEL}>Type</label>
              <select data-testid="trip-pet-type" className={FIELD} value={trip.pet_type || "dog"}
                onChange={(e) => set("pet_type", e.target.value)}>
                <option value="dog">Dog</option><option value="cat">Cat</option><option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className={LABEL}>Size</label>
              <select data-testid="trip-pet-size" className={FIELD} value={trip.pet_size || "medium"}
                onChange={(e) => set("pet_size", e.target.value)}>
                <option value="small">Small</option><option value="medium">Medium</option><option value="large">Large</option>
              </select>
            </div>
            <label className="sm:col-span-3 flex items-center gap-2 text-sm text-charcoal-600">
              <input data-testid="trip-pet-special" type="checkbox" checked={!!trip.pet_special_handling}
                onChange={(e) => set("pet_special_handling", e.target.checked)} className="accent-terracotta" />
              Special handling needed
            </label>
          </div>
        )}
      </div>

      {/* Add-on services */}
      {addons.length > 0 && (
        <div>
          <label className={LABEL}>Add-on services</label>
          <div className="grid sm:grid-cols-2 gap-2">
            {addons.map((a) => (
              <label key={a.key} data-testid={`trip-addon-${a.key}`}
                className={`flex items-start gap-2 rounded-lg border p-3 cursor-pointer transition-colors ${
                  selected.has(a.key) ? "border-terracotta bg-terracotta-light/40" : "border-sand-200 hover:border-sand-300"}`}>
                <input type="checkbox" checked={selected.has(a.key)} onChange={() => toggleAddon(a.key)}
                  className="mt-0.5 accent-terracotta" />
                <span>
                  <span className="text-sm font-medium text-charcoal-800">{a.name}</span>
                  {a.description && <span className="block text-xs text-charcoal-500">{a.description}</span>}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className={LABEL}>Special travel needs / notes (optional)</label>
        <textarea data-testid="trip-notes" rows={2} className={FIELD} value={trip.special_needs || ""}
          onChange={(e) => set("special_needs", e.target.value)} />
      </div>
    </div>
  );
}
